/**
 * Combined Cart Upsell Slider & Optimized Variant Switcher
 * File: assets/cart-upsell-list.js
 * Fixed to work after product toggle AND cart icon clicks
 * FIX (this version): on load, force each upsell card to show ITS OWN
 * default/selected variant image instead of whatever image happened to be
 * in the markup — which was bleeding through as the main PDP's variant
 * image when the upsell product matched the product being viewed.
 */
(function() {
  'use strict';

  if (window.CartUpsellCombinedLoaded) return;
  window.CartUpsellCombinedLoaded = true;

  let sliderInstances = new Map();

  // SLIDER FUNCTIONALITY (optimized)
  function initSlider(carousel) {
    if (sliderInstances.has(carousel)) {
      sliderInstances.get(carousel).destroy();
      sliderInstances.delete(carousel);
    }

    const wrapper = carousel.closest('.cart-upsell-carousel-wrapper');
    const [prevBtn, nextBtn] = [wrapper?.querySelector('.prev-next-button--prev'), wrapper?.querySelector('.prev-next-button--next')];

    if (!prevBtn || !nextBtn) return;

    const slides = carousel.querySelectorAll('.upsell_product');
    if (slides.length <= 1) {
      prevBtn.style.display = nextBtn.style.display = 'none';
      return;
    }

    let currentIndex = 0;

    // FIX: maxIndex is now computed live instead of cached once at init time.
    // Caching it once caused stale/wrong values if the cart drawer was still
    // animating open (offsetWidth 0 or mid-transition) when initSlider ran,
    // which made the next/prev arrows look clickable but silently do nothing.
    const getMaxIndex = () => {
      const currentSlideWidth = slides[0].offsetWidth + 10;
      if (!currentSlideWidth) return 0;
      return Math.max(0, slides.length - Math.floor(carousel.offsetWidth / currentSlideWidth));
    };

    // NEW: flag + timer for manual-swipe snapping
    let isProgrammaticScroll = false;
    let scrollEndTimer = null;

    // FIX: shared "which slide is closest to current scroll position" logic,
    // used both on scroll and to sync currentIndex right after init. This
    // matters because the carousel element (and its scrollLeft) usually
    // survives closing/reopening the cart drawer — only initSlider() re-runs.
    // Without this, currentIndex always restarted at 0 regardless of where
    // the carousel was actually scrolled to, causing next/prev to move to
    // the wrong slide or refuse to move at all after reopening the drawer.
    const getClosestIndex = () => {
      let closest = 0, minDiff = Infinity;
      slides.forEach((s, i) => {
        const diff = Math.abs(s.offsetLeft - carousel.scrollLeft);
        if (diff < minDiff) { minDiff = diff; closest = i; }
      });
      return closest;
    };

    const updateButtons = () => {
      const scrollLeft = carousel.scrollLeft;
      const maxScroll = carousel.scrollWidth - carousel.clientWidth;

      prevBtn.disabled = scrollLeft <= 0;
      nextBtn.disabled = scrollLeft >= maxScroll - 1;
      prevBtn.style.display = nextBtn.style.display = '';
    };

    const scrollToSlide = (index) => {
      isProgrammaticScroll = true; // NEW
      carousel.scrollTo({ left: slides[index].offsetLeft, behavior: 'smooth' });
      currentIndex = index;
      setTimeout(() => {
        isProgrammaticScroll = false; // NEW
        updateButtons();
      }, 300);
    };

    const handlePrevClick = (e) => {
      e.preventDefault();
      if (currentIndex > 0) scrollToSlide(currentIndex - 1);
    };

    const handleNextClick = (e) => {
      e.preventDefault();
      if (currentIndex < getMaxIndex()) scrollToSlide(currentIndex + 1); // FIX: live maxIndex
    };

    const handleScroll = () => {
      currentIndex = Math.max(0, Math.min(getClosestIndex(), getMaxIndex())); // FIX: live maxIndex
      updateButtons();

      // NEW: don't snap while a programmatic (button) scroll is animating
      if (isProgrammaticScroll) return;

      // NEW: wait until the user's swipe momentum settles, then snap
      clearTimeout(scrollEndTimer);
      scrollEndTimer = setTimeout(() => {
        scrollToSlide(currentIndex);
      }, 120);
    };

    prevBtn.addEventListener('click', handlePrevClick);
    nextBtn.addEventListener('click', handleNextClick);
    carousel.addEventListener('scroll', handleScroll);

    sliderInstances.set(carousel, {
      destroy() {
        prevBtn.removeEventListener('click', handlePrevClick);
        nextBtn.removeEventListener('click', handleNextClick);
        carousel.removeEventListener('scroll', handleScroll);
        clearTimeout(scrollEndTimer); // NEW: avoid leaking a snap after teardown
      }
    });

    // FIX: sync currentIndex to wherever the carousel is actually scrolled
    // to right now, instead of assuming it starts at slide 0. This is the
    // key fix for next/prev misbehaving after closing and reopening the
    // cart drawer (see getClosestIndex comment above for why).
    currentIndex = Math.max(0, Math.min(getClosestIndex(), getMaxIndex()));

    updateButtons();
  }

  // VARIANT SWITCHER (optimized)

  // COMPLETE THE SET: keeps the "Color: {{ }}" label under the title in
  // sync with whichever swatch actually ends up checked/selected on a
  // card. No-op on every card except the Complete the Set card, since
  // [data-cts-color] only exists there.
  function updateColorLabel(card, swatch) {
    if (!card || !swatch) return;
    const label = card.querySelector('[data-cts-color]');
    if (!label) return;
    const colorName = swatch.value || swatch.getAttribute('title') || '';
    if (colorName) label.textContent = 'Color: ' + colorName;
  }

  // FIX: pulled the "apply a variant to a card" logic out of the change-event
  // handler so it can be reused both on swatch click AND on page load.
  function applyVariantToCard(card, variantId) {
    if (!card || !variantId) return;

    const productId = card.dataset.productId;
    const variantInput = card.querySelector('[data-upsell-variant-id]');
    const mediaCarousel = card.querySelector('[data-media-carousel]');

    if (!variantInput || !mediaCarousel || !productId) return;

    const variantMediaMap = getVariantMediaMap(productId);
    const variantData = variantMediaMap?.[variantId];

    if (!variantData) return;

    // Match the displayed price to the exact variant submitted to the cart.
    const priceBox = card.querySelector('.upsell-card__price');
    if (priceBox && variantData.formattedPrice) {
      const onSale = variantData.compareAtPrice > variantData.price;
      priceBox.querySelector('.price')?.classList.toggle('price--on-sale', onSale);
      priceBox.querySelectorAll('.price__regular .price-item, .price-item--sale').forEach(el => {
        el.textContent = variantData.formattedPrice;
      });
      const sale = priceBox.querySelector('.price__sale');
      if (sale) {
        sale.style.display = onSale ? '' : 'none';
        const compare = sale.querySelector('.price-item--regular');
        if (compare) compare.textContent = variantData.formattedCompareAtPrice;
      }
    }

    variantInput.value = variantId;
    variantInput.disabled = variantData.available === false;

    if (variantData.src) {
      switchActiveImage(mediaCarousel, variantData.mediaId, variantData.src, variantData.srcset);
    }
  }

  function handleVariantChange(event) {
    const swatch = event.target;
    const card = swatch.closest('.cart-recommendations-container [data-upsell-card]');
    if (!card) return;

    applyVariantToCard(card, swatch.dataset.variantId);
    updateColorLabel(card, swatch);
  }

  // NEW: on load, each upsell card must show ITS OWN default/selected
  // variant image — never the main PDP's currently viewed variant, even
  // if the upsell card happens to be for the same product. This runs once
  // per card and re-derives the correct variantId from the card itself.
  function getCardsOwnSelectedVariantId(card) {
    // Try the most common ways a card marks "this swatch is the active one".
    // ADJUST THESE SELECTORS if your swatch markup differs.
    const selectedSwatch = card.querySelector(
      '.product-card-color-swatch:checked, ' +
      '.product-card-color-swatch[data-selected="true"], ' +
      '.product-card-color-swatch.is-selected, ' +
      '.product-card-color-swatch[aria-checked="true"]'
    );

    if (selectedSwatch?.dataset.variantId) {
      return selectedSwatch.dataset.variantId;
    }

    // Fall back to the first swatch rendered on the card, if any.
    const firstSwatch = card.querySelector('.product-card-color-swatch');
    if (firstSwatch?.dataset.variantId) {
      return firstSwatch.dataset.variantId;
    }

    // Last resort: whatever is already in the hidden variant input.
    const variantInput = card.querySelector('[data-upsell-variant-id]');
    return variantInput?.value || null;
  }

  function initUpsellVariantImages() {
    const cards = document.querySelectorAll('.cart-recommendations-container [data-upsell-card]');
    cards.forEach((card) => {
      const variantId = getCardsOwnSelectedVariantId(card);
      if (variantId) applyVariantToCard(card, variantId);

      const checkedSwatch = card.querySelector('.product-card-color-swatch:checked');
      if (checkedSwatch) updateColorLabel(card, checkedSwatch);
    });
  }

  // CART DRAWER CROSS-SELL COLOR PERSONALIZATION
  //
  // After each card has been set to its own default variant/image above,
  // try to override that with a variant matching the color of whatever the
  // customer most recently added to the cart. Runs independently per card:
  // a card with no matching color (or no color options at all) is simply
  // left showing whatever initUpsellVariantImages() already set, which is
  // exactly the required fallback behavior.
  function getPersonalizationColor() {
    if (!window.NOBLCrossSellColorMatch) return '';

    // Primary source: the color of whichever item is CURRENTLY at the top
    // of the cart drawer (cart.items.first), computed fresh server-side on
    // every cart-drawer render - add, remove, or quantity change - in
    // snippets/cart-upsell-list.liquid. Because the Section Rendering API
    // re-renders this container from the live cart on every single cart
    // change, this value can never go stale - e.g. removing the top item
    // automatically re-points this at whatever is now at the top, with no
    // extra bookkeeping required.
    //
    // FIX: this used to be secondary to a sessionStorage "last added color"
    // cache (see cart-cross-sell-color-match.js), which was only ever
    // updated on a successful add and never invalidated on removal. That
    // meant removing the most-recently-added colored item left the cache
    // pointing at a color that might not even be in the cart anymore, and
    // the cards would keep matching it. Reading the live server-rendered
    // value first fixes that.
    const container = document.querySelector('.cart-recommendations-container');
    const topItemColor = container && container.dataset.fallbackColor;
    if (topItemColor) return topItemColor;

    // Last-resort fallback: the color captured by product-form.js at the
    // moment of the most recent successful add-to-cart. Only used if the
    // cart-recommendations-container isn't present with its data attribute
    // for some reason (e.g. this script running before the drawer markup
    // exists).
    return window.NOBLCrossSellColorMatch.getLastAddedColor() || '';
  }

  function applyCrossSellColorPersonalization() {
    if (!window.NOBLCrossSellColorMatch) return;

    const color = getPersonalizationColor();
    if (!color) return;

    const normalizedTarget = window.NOBLCrossSellColorMatch.normalize(color);
    if (!normalizedTarget) return;

    const cards = document.querySelectorAll('.cart-recommendations-container [data-upsell-card]');
    cards.forEach((card) => {
      const swatches = Array.from(card.querySelectorAll('.product-card-color-swatch'));
      // No color options on this cross-sell product - nothing to match,
      // leave its existing/default variant behavior untouched.
      if (!swatches.length) return;

      const match = swatches.find(
        (swatch) => window.NOBLCrossSellColorMatch.normalize(swatch.value) === normalizedTarget
      );
      // This product doesn't offer the added color - fall back to its own
      // default/current variant (do nothing further).
      if (!match) return;

      const productId = card.dataset.productId;
      const variantMediaMap = getVariantMediaMap(productId);
      const variantData = variantMediaMap ? variantMediaMap[match.dataset.variantId] : null;

      // Color-matched variant to use, unless it turns out to be sold out
      // (handled just below).
      let target = match;

      if (variantData && variantData.available === false) {
        // The color-matched variant is sold out. Rather than silently
        // falling back to the card's existing default swatch - which is
        // just whichever color happens to be listed first and may itself
        // be sold out - look for the first swatch on this card that IS
        // in stock and use that instead, so the customer never sees (or
        // can add) an unavailable variant.
        const availableSwatch = findFirstAvailableSwatch(swatches, variantMediaMap);
        // Every color for this product is sold out - nothing usable to
        // switch to, so leave the card exactly as initUpsellVariantImages()
        // already set it.
        if (!availableSwatch) return;
        target = availableSwatch;
      }

      if (!target.checked) {
        swatches.forEach((swatch) => {
          swatch.checked = swatch === target;
        });
      }

      // Reuse the same function manual swatch clicks use, so the image and
      // the hidden add-to-cart variant id stay in sync with what's shown.
      applyVariantToCard(card, target.dataset.variantId);
      updateColorLabel(card, target);
    });
  }

  // Returns the first swatch (in the order the card renders them) whose
  // variant is not explicitly marked sold out. A variant missing from the
  // media map is treated as available, matching the assumption already used
  // above for the color-matched variant itself.
  function findFirstAvailableSwatch(swatches, variantMediaMap) {
    for (const swatch of swatches) {
      const variantData = variantMediaMap ? variantMediaMap[swatch.dataset.variantId] : null;
      if (!variantData || variantData.available !== false) {
        return swatch;
      }
    }
    return null;
  }

  function getVariantMediaMap(productId) {
    const scriptTag = document.querySelector(`[data-variant-media-map-${productId}]`);
    if (!scriptTag) return null;

    try {
      return JSON.parse(scriptTag.textContent);
    } catch (e) {
      console.warn('Failed to parse variant media map:', e);
      return null;
    }
  }

  function switchActiveImage(mediaCarousel, newMediaId, newSrc, newSrcset) {
    if (!mediaCarousel) return;
    const img = mediaCarousel.querySelector('.upsell-card__image--active');
    if (!img) return;
    if (newSrc) img.src = newSrc;
    if (newSrcset) img.srcset = newSrcset;
    if (newMediaId) img.dataset.mediaId = newMediaId;
  }
  // COMPLETE THE SET: the restriction that marks a swatch
  // `upsell-variant-restricted-swatch` is injected by a third-party app
  // client-side, AFTER Shopify's server-rendered HTML - so Liquid has no
  // way to know about it at render time. This checks for it after the
  // page (and the app) have settled, and if the currently-matched color's
  // swatch is restricted, strips all Complete the Set styling/behavior
  // from that card so it falls back to looking like a normal upsell.
  function revertCompleteTheSetIfRestricted() {
    const card = document.querySelector('.cart-recommendations-container [data-complete-the-set="true"]');
    if (!card) return;

    const checkedSwatch = card.querySelector('.product-card-color-swatch:checked');
    if (!checkedSwatch) return;

    if (checkedSwatch.classList.contains('upsell-variant-restricted-swatch')) {
      card.classList.remove('upsell-card_complete-the-set');
      card.removeAttribute('data-complete-the-set');

      const colorLabel = card.querySelector('[data-cts-color]');
      if (colorLabel) colorLabel.remove();

      const submitBtn = card.querySelector('.quick-add__submit');
      if (submitBtn) {
        const label = submitBtn.querySelector('[data-upsell-btn-label]');
        if (label) label.textContent = 'Add to cart';
      }
    }
  }

  // INITIALIZATION
  function init() {
    const carousels = document.querySelectorAll('.cart-drawer__recommendations scroll-carousel#carousel');
    carousels.forEach(carousel => setTimeout(() => initSlider(carousel), 50));

    // NEW: correct each upsell card's image right after (re)render.
    setTimeout(initUpsellVariantImages, 50);

    // Then try to personalize each card's color to match whatever was
    // most recently added to the cart. Must run after
    // initUpsellVariantImages so it overrides the per-card default only
    // when a real match is found.
    setTimeout(applyCrossSellColorPersonalization, 60);

    // Run after the above AND after giving the third-party app a moment
    // to inject its upsell-variant-restricted-swatch class.
    setTimeout(revertCompleteTheSetIfRestricted, 250);

    if (!window.variantSwitcherInitialized) {
      document.addEventListener('change', function(event) {
        if (event.target.matches('.product-card-color-swatch')) {
          handleVariantChange(event);
        }
      });
      window.variantSwitcherInitialized = true;
    }
  }

  function cleanup() {
    sliderInstances.forEach(instance => instance.destroy());
    sliderInstances.clear();
  }

  let initTimeout;
  const debouncedInit = () => {
    clearTimeout(initTimeout);
    initTimeout = setTimeout(() => {
      cleanup();
      init();
    }, 150);
  };

  // FIX: Check if cart drawer is visible and reinitialize
  const checkCartDrawerAndInit = () => {
    const cartDrawer = document.querySelector('#cart-drawer, .cart-drawer, [data-cart-drawer]');
    if (cartDrawer) {
      const isVisible = cartDrawer.classList.contains('active') ||
                       cartDrawer.classList.contains('open') ||
                       cartDrawer.style.display !== 'none' ||
                       getComputedStyle(cartDrawer).display !== 'none';

      if (isVisible) {
        // Wait for content to load then initialize
        setTimeout(debouncedInit, 200);
      }
    }
  };

  // Initialize when ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Handle cart updates
  document.addEventListener('cart:updated', debouncedInit);
  document.addEventListener('cart:refresh', debouncedInit);
  document.addEventListener('scripts:reloaded', debouncedInit);

  // FIX: Listen for cart drawer opens (cart icon clicks)
  document.addEventListener('click', function(event) {
    // Check if clicked element or its parent is a cart trigger
    const cartTrigger = event.target.closest('[href="/cart"], [data-cart-trigger], .cart-link, .header__icon--cart, [onclick*="cart"]');
    if (cartTrigger) {
      setTimeout(checkCartDrawerAndInit, 100);
    }
  });

  // FIX: Listen for drawer state changes
  const cartStateObserver = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.type === 'attributes' &&
          (mutation.attributeName === 'class' || mutation.attributeName === 'style')) {
        const target = mutation.target;
        if (target.matches('#cart-drawer, .cart-drawer, [data-cart-drawer]')) {
          checkCartDrawerAndInit();
        }
      }
    });
  });

  // Observe cart drawer for visibility changes
  const cartDrawer = document.querySelector('#cart-drawer, .cart-drawer, [data-cart-drawer]');
  if (cartDrawer) {
    cartStateObserver.observe(cartDrawer, {
      attributes: true,
      attributeFilter: ['class', 'style']
    });

    // Also observe for content changes
    const contentObserver = new MutationObserver(function(mutations) {
      const shouldReinit = mutations.some(mutation =>
        mutation.type === 'childList' &&
        Array.from(mutation.addedNodes).some(node =>
          node.nodeType === 1 &&
          (node.classList?.contains('cart-recommendations-container') ||
           node.querySelector?.('scroll-carousel#carousel'))
        )
      );

      if (shouldReinit) debouncedInit();
    });

    contentObserver.observe(cartDrawer, {
      childList: true,
      subtree: true
    });
  }

  window.cleanupUpsellSliders = cleanup;
})();